



































































import moment from 'moment';
import { AttachmentType } from '@getaccept/lib-shared/src/attachments/types/attachment-type';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import ThemedButton from '@getaccept/lib-shared/src/components/ThemedButton.vue';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { PropType } from '@vue/composition-api';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ParagraphSize } from '@getaccept/lib-ui';
import { IconHelper } from '@getaccept/lib-shared/src/helpers/icon.helper';
import { DURATION } from '../../common/constants/tooltip';

export default defineComponent({
  components: {
    ThemedButton,
  },
  props: {
    documentAttachment: {
      type: Object as PropType<DocumentAttachment>,
      required: true,
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: () => ({}),
    },
    isSigned: {
      type: Boolean,
    },
    isPreview: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const focused = ref(false);
    const showPreviewTooltip = ref(false);
    const hovered = ref(false);

    const watchFocusedRequiredId = (focusedRequiredId: string) => {
      if (props.documentAttachment.id === focusedRequiredId) {
        setFocus(true);
      } else {
        setFocus(false);
      }
    };

    watch(() => props.focusedRequiredId, watchFocusedRequiredId);

    const showDescription = computed(() => {
      if (props.documentAttachment.attachment.type !== AttachmentType.Upload) {
        return props.documentAttachment.requireView && !props.documentAttachment.lastView;
      }
      return !!props.documentAttachment.attachment.description;
    });

    const showInteraction = computed(() => {
      if (props.documentAttachment.attachment.type === AttachmentType.Upload) {
        return !!props.documentAttachment.lastUpload;
      }
      return !!props.documentAttachment.lastView;
    });

    const description = computed(() => props.documentAttachment.attachment.description);

    const requiredMessage = computed(() => {
      if (props.isPreview || !props.documentAttachment.requireView) {
        return null;
      }
      return props.documentAttachment.attachment.type === AttachmentType.Upload
        ? t('required-upload')
        : t('requires-view');
    });

    const showTooltip = computed(() => {
      if (!requiredMessage.value) {
        return false;
      }
      if (props.documentAttachment.lastView || props.documentAttachment.lastUpload) {
        return false;
      }
      return focused.value;
    });

    const attachmentInteraction = computed(() => {
      if (props.documentAttachment.attachment.type === AttachmentType.Upload) {
        return `${t('uploaded-file')}: ${props.documentAttachment.attachment.filename}, ${moment(
          props.documentAttachment.lastUpload
        ).format('L')}`;
      }
      return `${t('viewed-at')} ${moment(props.documentAttachment.lastView).format('hh:mm A L')}`;
    });

    const icon = computed(() => {
      switch (props.documentAttachment.attachment.type) {
        case AttachmentType.Upload:
          return 'upload';
        case AttachmentType.External:
          return 'link';
        default:
          return IconHelper.getFileIcon(props.documentAttachment.attachment.filename);
      }
    });

    const title = computed(() => {
      if (props.documentAttachment.attachment.title) {
        return props.documentAttachment.attachment.title;
      }
      return t('attachment-title-not-found');
    });

    const buttonText = computed(() => {
      if (props.documentAttachment.attachment.type === AttachmentType.Upload && !props.isSigned) {
        return !props.documentAttachment.lastUpload ? t('upload') : t('replace');
      }
      return t('show');
    });

    const disabled = computed(
      () => props.isPreview && props.documentAttachment.attachment.type === AttachmentType.Upload
    );

    const handleClick = (documentAttachment: DocumentAttachment) => {
      const { id: documentAttachmentId, previewUrl, attachment } = documentAttachment;
      const { id, type, title } = attachment;
      switch (type) {
        case AttachmentType.Upload:
          if (props.isSigned) {
            openAttachmentInNewTab(documentAttachmentId, previewUrl);
            return;
          }
          if (props.isPreview) {
            togglePreviewTooltip();
            return;
          }
          emit('uploadFile', {
            attachmentId: id,
            name: title,
          });
          break;
        case AttachmentType.External:
        case AttachmentType.File:
        case AttachmentType.Document:
          if (previewUrl) {
            openAttachmentInNewTab(documentAttachmentId, previewUrl);
          }
      }
    };

    const openAttachmentInNewTab = (id: string, url: string) => {
      window.open(url);
      emit('attachmentView', id);
    };

    const setFocus = (hasFocus: boolean) => {
      focused.value = hasFocus;
    };

    const setHover = (hasHover: boolean) => {
      hovered.value = hasHover;
    };

    const togglePreviewTooltip = () => {
      if (showPreviewTooltip.value) {
        return;
      }
      showPreviewTooltip.value = true;
      setTimeout(() => {
        showPreviewTooltip.value = false;
      }, DURATION);
    };

    return {
      focused,
      showDescription,
      showInteraction,
      description,
      requiredMessage,
      showTooltip,
      attachmentInteraction,
      icon,
      title,
      buttonText,
      disabled,
      handleClick,
      setFocus,
      setHover,
      hovered,
      showPreviewTooltip,
      ParagraphSize,
      t,
      togglePreviewTooltip,
      openAttachmentInNewTab,
      watchFocusedRequiredId,
    };
  },
});
